import { MeshBuilder, BackgroundMaterial, Color4, CubeTexture, DirectionalLight, GIRSM, GIRSMManager, Mesh, MirrorTexture, Plane, ReflectiveShadowMap, Texture, Vector3, HDRCubeTexture, BaseTexture, Scene } from "@babylonjs/core";
import { View } from "./view";
import { ModelLoader } from "./modelLoader";

//see https://www.babylonjs-playground.com/#NWNUIH#1 for more
export class Environment{

    public EnvironmentPath: string;

    private _backgroundPath: string;
    get BackgroundPath(): string {
        return this._backgroundPath;
    }
    set BackgroundPath(value: string) {
        this._backgroundPath = value;
    }

    private _environmentUsingBackgroundMap: boolean = true;
    get EnvironmentUsingBackgroundMap(): boolean {
        return this._environmentUsingBackgroundMap;
    }
    set EnvironmentUsingBackgroundMap(value: boolean) {
        this._environmentUsingBackgroundMap = value;
    }
    private _backgroundUsingColor: boolean;
    get BackgroundUsingColor(): boolean {
        return this._backgroundUsingColor;
    }
    set BackgroundUsingColor(value: boolean) {
        this._backgroundUsingColor = value;
    }

    
    private _blur: number;
    get Blur(): number {
        return this._blur;
    }
    set Blur(value: number) {
        this._blur = value;
        //if (this.BackgroundPath)
        //    this.ApplyEnvironment(this.BackgroundPath);
    }

    private _intensity: number = 1;
    get Intensity(): number {
        return this._intensity;
    }
    set Intensity(value: number) {
        this._intensity = value;
    }

    private _skyBox: Mesh;
    private _view: View;
    private _scene: Scene;
    private _modelLoader: ModelLoader;

    constructor(view: View, scene: Scene, modelLoader: ModelLoader) //TODO replace this viewDetails
    {
        this._view = view
        this._scene = scene
        this._modelLoader = modelLoader;
    
    }

    private RemoveSkyBox()
    {
        if (this._skyBox)
        {
            this._scene.removeMesh(this._skyBox);
            this._skyBox = null;
        }
    }

    private LoadCubeTexture(path: string): BaseTexture
    {
        const envBackgroundExtension = this._view.GetFilenameExtensionFromUri(path);
        let texture: BaseTexture;
        if (envBackgroundExtension === "env")
        {
            texture = new CubeTexture(
                path, 
                this._scene,
                null, 
                false, 
                null, 
                () => console.log("Loaded ", path), 
                (message, exception) => { 
                    console.log ("skybox load error: ", message, exception);
                },
                null,
                true
            );
        }
        else if (envBackgroundExtension === "hdr")
        {
            const prefilterOnLoad = true;
            texture = new HDRCubeTexture(
                path,
                this._scene,
                256,
                false,
                false,
                false,
                prefilterOnLoad,
                () => console.log("Loaded ", path), 
                (message, exception) => { 
                    console.log ("skybox load error: ", message, exception);
                },
                true //supersample
            );                
        }

        return texture;
    }
    public ApplyEnvironment() : boolean
    {
        this._scene.environmentIntensity = this._intensity;
        
        if (this.BackgroundUsingColor)
        {
            this.RemoveSkyBox();
            return true;
        }
        
        const backgroundTexture = this.LoadCubeTexture(this.BackgroundPath);        
        if (!backgroundTexture)
        {
            this.RemoveSkyBox();
            if (this.EnvironmentUsingBackgroundMap)
            {
                this._scene.environmentTexture = null;
                return false;
            }
        }
        else
        {
            if (this.EnvironmentUsingBackgroundMap)
            {
                //also sets proper material and this._view.Scene.environmentTexture?
                //setGlobalEnvTexture: boolean has default true indicating that scene.environmentTexture must match the current skybox texture
                this.RemoveSkyBox(); //remove old
                this._skyBox = this._scene.createDefaultSkybox(backgroundTexture, true, 10000, this.Blur/100);
                return true;
            }
        }

        const environmentTexture = this.LoadCubeTexture(this.EnvironmentPath);                
        if (environmentTexture)
        {
            this._scene.environmentTexture = environmentTexture;
            return true;
        }

        return false;
    }

    public GlobalIluminations()
    {
        const rsm = new ReflectiveShadowMap(this._scene, this._modelLoader.light, { width: 512, height: 512 });
        rsm.addMesh();
        const outputDimensions = {
            width: this._view.Engine.getRenderWidth(true),
            height: this._view.Engine.getRenderHeight(true),
        };
        
        const defaultGITextureRatio = 2;
        
        const giTextureDimensions = {
            width: Math.floor(this._view.Engine.getRenderWidth(true) / defaultGITextureRatio),
            height: Math.floor(this._view.Engine.getRenderHeight(true) / defaultGITextureRatio),
        };
        
        const giRSMMgr = new GIRSMManager(this._scene, outputDimensions, giTextureDimensions, 2048);
        const grms = new GIRSM(rsm)
        giRSMMgr.addGIRSM(grms);
        giRSMMgr.addMaterial(); // no s
    }

    public ConfiguratorScene()
    {
        const light = new DirectionalLight("light1", new Vector3(-1, -3, 1), this._scene);
        light.position = new Vector3(10, 20, 10);
        light.intensity = 0.7;

        const ground = MeshBuilder.CreateGround("ground1", {width: 100, height:100, subdivisions: 2});
        ground.receiveShadows = true;

        const backgroundMaterial = new BackgroundMaterial("backgroundMaterial", this._scene);
        backgroundMaterial.diffuseTexture = new Texture(
            "https://assets.babylonjs.com/environments/backgroundGround.png",
            this._scene
            );
        backgroundMaterial.diffuseTexture.hasAlpha = true;
        backgroundMaterial.opacityFresnel = false;
        backgroundMaterial.shadowLevel = 0.4;

        const mirror = new MirrorTexture("mirror", 1024, this._scene);
        mirror.mirrorPlane = new Plane(0, -1, 0, 0);
        for(let i=1;i<this._scene.meshes.length-2;i++)
        {
            mirror.renderList.push(this._scene.meshes[i]);
        }
        
        backgroundMaterial.reflectionTexture = mirror;
        backgroundMaterial.reflectionFresnel = true;
        backgroundMaterial.reflectionStandardFresnelWeight = 0.8;

        ground.material = backgroundMaterial;
        this._scene.removeMesh(this._scene.meshes[this._scene.meshes.length]);
        this._scene.clearColor = new Color4(1,1,1,1);
    }
    public RemoveEnvironment()
    {
        if(this._skyBox)
            this._scene.removeMesh(this._skyBox);
    }
}